import {ReactElement, useCallback, useEffect, useMemo, useRef} from 'react';

import NavigationMenuListColumn from './NavigationMenuListColumn';
import {
  MainNavigationCategoryType,
  LinkTargetType,
  LinkListNavColumnType,
  FeatureLinkNavColumnType,
  MainNavigationCategoryName,
  ValueOf,
} from '../../types/backend';
import NavigationMenuFeatureLinkColumn from './NavigationMenuFeatureLinkColumn';
import {Link, Image} from '@shopify/hydrogen';
import classNames from 'classnames';

interface HeaderNavigationMenuProps {
  menu: MainNavigationCategoryType;
  isActive: boolean;
  setIsActive: (
    categoryName: MainNavigationCategoryType['category_name'],
  ) => void;
  onNavigate: () => void;
}

const categoryNameMapping: Record<
  ValueOf<typeof MainNavigationCategoryName>,
  string
> = Object.freeze({
  [MainNavigationCategoryName.whisky]: 'Shop Whisky',
  [MainNavigationCategoryName.eventsAndTastings]: 'Events & Tastings',
  [MainNavigationCategoryName.rewards]: 'Whisky Loyalty Program',
  [MainNavigationCategoryName.distilleriesAndBars]: 'Distilleries & Bars',
  [MainNavigationCategoryName.community]: 'Community',
  [MainNavigationCategoryName.newsAndReviews]: 'News & Reviews',
  [MainNavigationCategoryName.samplesAndGifts]: 'Samples & Gifts',
  [MainNavigationCategoryName.whiskyGuides]: 'Whisky Guides',
});

function isLinkListNavColumnType(
  column: LinkListNavColumnType | FeatureLinkNavColumnType,
): column is LinkListNavColumnType {
  return column.column_type === 'link_list';
}

function isFeatureLinkNavColumnType(
  column: LinkListNavColumnType | FeatureLinkNavColumnType,
): column is FeatureLinkNavColumnType {
  return column.column_type === 'feature_link';
}

export default function HeaderNavigationMenu({
  menu,
  isActive,
  setIsActive,
  onNavigate,
}: HeaderNavigationMenuProps): ReactElement {
  const menuRef = useRef<HTMLLIElement | null>(null);
  const menuClickHandler = useCallback(
    () => setIsActive(isActive ? null : menu.category_name),
    [isActive, menu.category_name, setIsActive],
  );

  const clickOutsideHandler = useCallback(
    (e) =>
      isActive && !menuRef?.current?.contains(e.target) && setIsActive(null),
    [isActive, setIsActive],
  );

  useEffect(() => {
    document.addEventListener('click', clickOutsideHandler);
    return () => document.removeEventListener('click', clickOutsideHandler);
  }, [clickOutsideHandler]);

  const menuCategoryNameLabel = useMemo(
    () => categoryNameMapping[menu?.category_name] ?? menu?.category_name,
    [menu?.category_name],
  );

  const isRewardsMenu = useMemo(
    () => menu.category_name === MainNavigationCategoryName.rewards,
    [menu.category_name],
  );

  if (isRewardsMenu) {
    return (
      <li className="main-menu__item">
        <Link to="/l/rewards" className="main-menu__link" onClick={onNavigate}>
          {menuCategoryNameLabel}
        </Link>
      </li>
    );
  }

  return (
    <li
      ref={menuRef}
      className={classNames('main-menu__item has-dropdown', {
        'active-menu': isActive,
      })}
    >
      <span
        role="button"
        tabIndex={-1}
        onClick={menuClickHandler}
        className="main-menu__link"
      >
        {menuCategoryNameLabel}
      </span>
      <div className="submenu">
        <div className="container">
          <div className="submenu__top">
            {menu?.columns?.map((column) => {
              if (isLinkListNavColumnType(column)) {
                return (
                  <NavigationMenuListColumn
                    key={column.uuid}
                    title={column.column_heading}
                    links={column.navigation_links}
                    onNavigate={onNavigate}
                  />
                );
              }
              if (isFeatureLinkNavColumnType(column)) {
                return (
                  <NavigationMenuFeatureLinkColumn
                    key={column.uuid}
                    featureLink={column}
                  />
                );
              }
            })}
            <NavigationBottom menu={menu} onNavigate={onNavigate} />
          </div>
        </div>
      </div>
    </li>
  );
}

interface BottomNavigationLinksProps {
  menu?: MainNavigationCategoryType;
  onNavigate: () => void;
}

function NavigationBottom({menu, onNavigate}: BottomNavigationLinksProps) {
  if (
    !(
      menu?.featured_link1_url ||
      menu?.featured_link2_url ||
      menu?.extra_link_url
    )
  ) {
    return;
  }
  return (
    <div className="submenu__bottom">
      <div className="submenu__bottom-links">
        {menu?.featured_link1_url && (
          <NavigationBottomFeatureLink
            url={menu?.featured_link1_url}
            image={menu?.featured_link1_image}
            text={menu?.featured_link1_text}
            target={menu?.featured_link1_target}
            onNavigate={onNavigate}
          />
        )}
        {menu?.featured_link2_url && (
          <NavigationBottomFeatureLink
            url={menu?.featured_link2_url}
            image={menu?.featured_link2_image}
            text={menu?.featured_link2_text}
            target={menu?.featured_link2_target}
            onNavigate={onNavigate}
          />
        )}
      </div>
      {menu?.extra_link_url && (
        <Link
          to={menu?.extra_link_url}
          target={menu?.extra_link_target}
          className="submenu__bottom-browse"
        >
          <span>{menu?.extra_link_text}</span>
          <i className="icon-right-arrow1" />
        </Link>
      )}
    </div>
  );
}

interface NavigationBottomFeatureLinkProps {
  url: string;
  image: string;
  text: string;
  target: LinkTargetType;
  onNavigate: () => void;
}

function NavigationBottomFeatureLink({
  url,
  image,
  text,
  target,
  onNavigate,
}: NavigationBottomFeatureLinkProps) {
  return (
    <div className="submenu__bottom-links__item">
      <Link
        to={url}
        target={target}
        onClick={onNavigate}
        className="submenu__bottom-links__image"
      >
        <picture>
          <source srcSet={image} />
          {image && <Image src={image} alt={text} width={103} height={83} />}
        </picture>
      </Link>
      <h3 className="submenu__bottom-links__title">
        <Link to={url} target={target} onClick={onNavigate}>
          {text}
        </Link>
      </h3>
    </div>
  );
}
